import { useMemo, useState } from 'react';
import useAuthStore from '@entities/auth/model/useAuthStore';
import { getDefaultDateRange, formatDateToYYYYMMDD } from '@shared/utils/formatDate';
import Alert from '@shared/components/atoms/Alert';

import AdminLayout from '@shared/components/organisms/AdminLayout/AdminLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import SearchFilter from '@shared/components/organisms/SearchFilter/SearchFilter';
import { useGetTeamsByCompanies } from '@features/team/hooks';
import { useAdminInquiryListQuery } from '@features/inquiry/hooks';
import useSort from '@shared/hooks/useSort';
import ApprovalDataTable from '@shared/components/organisms/ApprovalDataTable/ApprovalDataTable';
import Pagination from '@shared/components/organisms/Pagination/Pagination';
import InquiryDetailModal from './components/InquiryDetaiModal';
import useCompanyListQuery from '@features/company/hooks';

import styles from '@pages/admin/inquiry/InquiryManagementPage.module.scss';

export default function InquiryManagementPage() {
  //모든 회사
  const { data: companyList = [] } = useCompanyListQuery();

  // 부서쿼리
  const teamQueries = useGetTeamsByCompanies(companyList);
  //부서 결과 병합
  const allTeamList = useMemo(() => {
    return teamQueries.flatMap((q) => q.data ?? []);
  }, [teamQueries]);
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  const [alertState, setAlertState] = useState({
    isOpen: false,
    mode: 'alert', // 'alert' | 'confirm'
    message: '',
    onConfirm: null,
  });

  const [draftFilters, setDraftFilters] = useState({
    queryPeriod_start: defaultRange.start,
    queryPeriod_end: defaultRange.end,
    // teamCode: '',
    companyCode: '',
    progressStatus: '',
    name: '',
  });

  // 실제 조회 값
  const [searchParams, setSearchParams] = useState({
    startDate: defaultRange.start,
    endDate: defaultRange.end,
    // teamId: null,
    companyCode: null,
    status: null,
    name: null,
  });
  function handleFilterChange(key, value) {
    setDraftFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  // const teamOptions = useMemo(() => {
  //   return [
  //     { value: '', label: '전체' },
  //     ...allTeamList.map((team) => ({
  //       value: team.teamCode?.toString() || '',
  //       label: team.team || '',
  //     })),
  //   ];
  // }, [allTeamList]);

  const companyOptions = useMemo(() => {
    return [
      { value: '', label: '전체' },
      ...companyList.map((company) => ({
        value: company.companyCode?.toString() || '',
        label: company.company || '',
      })),
    ];
  }, [companyList]);
  const filterFields = useMemo(() => {
    return [
      {
        key: 'queryPeriod',
        type: 'dateRange',
        label: '조회기간',
        startKey: 'queryPeriod_start',
        endKey: 'queryPeriod_end',
        startValue: draftFilters.queryPeriod_start,
        endValue: draftFilters.queryPeriod_end,
      },
      { newRow: true },

      {
        key: 'companyCode',
        type: 'select',
        label: '문의회사',
        value: draftFilters.companyCode,
        options: companyOptions,
        fill: true,
      },
      {
        key: 'progressStatus',
        type: 'select',
        label: '진행상태',
        value: draftFilters.progressStatus,
        options: [
          { value: '', label: '전체' },
          { value: '0', label: '접수' },
          { value: '1', label: '처리중' },
          { value: '2', label: '처리완료' },
          { value: '3', label: '반려' },
        ],
        fill: true,
      },
      { newRow: true },

      {
        key: 'name',
        type: 'input',
        label: '문의자',
        value: draftFilters.name,
        fill: true,
      },
      {
        key: 'inquiryType',
        type: 'select',
        label: '문의 유형',
        value: draftFilters.inquiryType,
        options: [
          { value: '', label: '전체' },
          { value: '1', label: '서비스 불편 / 제안' },
          { value: '2', label: '시스템 오류 (PC/Mobile)' },
          { value: '3', label: '서비스 개선 제안' },
          { value: '4', label: '기타' },
        ],
        fill: true,
      },
    ];
  }, [draftFilters, companyOptions]);
  //문의 목록 좋회
  const { data: inquiryList = [], isLoading, refetch } = useAdminInquiryListQuery(searchParams, {});

  function handleSearch() {
    setSearchParams({
      startDate: draftFilters.queryPeriod_start,
      endDate: draftFilters.queryPeriod_end,
      // teamId: draftFilters.teamCode ? Number(draftFilters.teamCode) : null,
      companyCode: draftFilters.companyCode ? Number(draftFilters.companyCode) : null,
      status: draftFilters.progressStatus ? Number(draftFilters.progressStatus) : null,
      inquiryType: draftFilters.inquiryType ? Number(draftFilters.inquiryType) : null,
      name: draftFilters.name || null,
    });
  }

  const tableColumns = useMemo(
    () => [
      {
        key: 'inquiryDate',
        label: '문의일자',
        width: '8%',
        render(value) {
          return formatDateToYYYYMMDD(value);
        },
      },
      {
        key: 'inquiryType',

        label: '문의 유형',
        width: '12%',
        render(value) {
          const map = {
            1: '서비스 불편 / 제안',
            2: '시스템 오류 (PC/Mobile)',
            3: '서비스 개선 제안',
            4: '기타',
          };
          return map[value] ?? '-';
        },
      },
      { key: 'inquiryContent', label: '문의내용', width: '27%' },
      {
        key: 'companyName',
        label: '회사',
        width: '8%',
      },
      { key: 'teamName', label: '문의부서', width: '10%' },
      { key: 'writerName', label: '문의자', width: '8%' },
      { key: 'comment', label: '답변', width: '12%' },
      {
        key: 'status',
        label: '진행상태',
        width: '7%',
        render(value) {
          const map = {
            0: '접수',
            1: '처리중',
            2: '처리완료',
            3: '반려',
          };
          return map[value] ?? '-';
        },
      },
    ],
    [],
  );
  const companyMap = useMemo(() => {
    return Object.fromEntries(companyList.map((c) => [c.companyCode, c.company]));
  }, [companyList]);

  const teamMap = useMemo(() => {
    return Object.fromEntries(allTeamList.map((t) => [t.teamCode, t.team]));
  }, [allTeamList]);

  const tableData = useMemo(() => {
    return inquiryList.map((item) => ({
      ...item,
      teamName: teamMap[item.teamId] || '-',
      companyName: companyMap[item.companyCode] || '-',
    }));
  }, [inquiryList, teamMap, companyMap]);

  const { sortedData, sortField, sortDirection, handleSort } = useSort(tableData, {
    field: 'inquiryDate',
    direction: 'desc',
  });

  const paginatedData = useMemo(() => {
    return sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [sortedData, currentPage]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  function handleRowClick(item) {
    setSelectedInquiry(item);
    setIsModalOpen(true);
  }
  function handleReset() {
    // 1️⃣ UI 입력값 초기화
    setDraftFilters({
      queryPeriod_start: defaultRange.start,
      queryPeriod_end: defaultRange.end,
      // teamCode: '',
      companyCode: '',
      progressStatus: '',
      inquiryType: '',
      name: '',
    });

    // 2️⃣ 실제 조회 조건 초기화 (React Query 자동 재조회)
    setSearchParams({
      startDate: defaultRange.start,
      endDate: defaultRange.end,
      // teamId: null,
      companyCode: null,
      status: null,
      inquiryType: null,
      name: null,
    });

    // 3️⃣ 페이지 초기화
    setCurrentPage(1);
  }

  return (
    <>
      <AdminLayout>
        <TabContentLayout
          tabs={{
            label: '문의사항 관리',
            content: (
              <ContentCard className={styles.contentCardMarginFix}>
                {/* 검색/필터 섹션 */}
                <div className={styles.filterWrapper}>
                  <SearchFilter
                    fields={filterFields}
                    onFilterChange={handleFilterChange}
                    onSearch={handleSearch}
                    onReset={handleReset}
                    documnetCount={tableData.length}
                  />
                </div>
                {/* 데이터 테이블 */}
                <div className={styles.tableWrapper}>
                  <>
                    <ApprovalDataTable
                      columns={tableColumns}
                      data={paginatedData}
                      onRowClick={handleRowClick}
                      onSort={handleSort}
                      sortField={{ field: sortField, direction: sortDirection }}
                      getRowKey={(item, index) => `${item.id}_${index}`}
                      emptyContent={<div className={styles.emptyCell}>문서가 없습니다.</div>}
                    />
                  </>
                </div>
                {/* 페이지네이션 */}
                <div className={styles.paginationWrapper}>
                  {tableData.length > 0 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                      onFirstPage={() => setCurrentPage(1)}
                      onPrevPage={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      onNextPage={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      onLastPage={() => setCurrentPage(totalPages)}
                    />
                  )}
                </div>
              </ContentCard>
            ),
          }}
        />
      </AdminLayout>
      {isModalOpen && (
        <InquiryDetailModal
          isOpen={isModalOpen}
          inquiryData={selectedInquiry}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedInquiry(null);
          }}
          onUpdated={() => {
            refetch(); // 목록 재조회
            setAlertState({
              isOpen: true,
              mode: 'alert',
              message: '답변이 등록되었습니다.',
              onConfirm: null,
            });
          }}
          onError={() => {
            setAlertState({
              isOpen: true,
              mode: 'alert',
              message: '수정 중 오류가 발생했습니다.',
              onConfirm: null,
            });
          }}
          onAlert={(message) =>
            setAlertState({
              isOpen: true,
              mode: 'alert',
              message,
              onConfirm: null,
            })
          }
        />
      )}
      <Alert
        isOpen={alertState.isOpen}
        mode={alertState.mode}
        message={alertState.message}
        onConfirm={alertState.onConfirm}
        onClose={() =>
          setAlertState((prev) => ({
            ...prev,
            isOpen: false,
          }))
        }
      />
    </>
  );
}
